import React, { useState, useEffect } from 'react';

export const CronGenerator = ({
  valueUpdate,
  onChange = () => {},
  allowEmpty = true,
  disabled = false,
}) => {
  const [primarySelection, setPrimarySelection] = useState('once');
  const [monthSelections, setMonthSelections] = useState([]);
  const [daySelections, setDaySelections] = useState([]);
  const [weekdaySelections, setWeekdaySelections] = useState([]);
  const [hourSelections, setHourSelections] = useState([]);
  const [minuteSelections, setMinuteSelections] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [timeSelections, setTimeSelections] = useState([]);
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
  const [currentTimeInput, setCurrentTimeInput] = useState('');
  const [value, setValue] = useState(valueUpdate? valueUpdate : '');

  useEffect(() => {
    if (valueUpdate && !valueUpdate.includes('NaN')) {
          console.log('Value Update:', valueUpdate);

      setValue(valueUpdate);
    }
  }, [valueUpdate]);
  

  const primaryOptions = [
    { id: 'once', name: 'Once' },
    { id: 'day', name: 'Daily' },
    { id: 'week', name: 'Weekly' },
    { id: 'month', name: 'Monthly' },
    { id: 'year', name: 'Yearly' },
    { id: 'hour', name: 'Hourly' },
    { id: 'minute', name: 'Every Minute' },

  ];

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    id: (i + 1).toString(),
    name: new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' }),
  }));

  const dayOptions = Array.from({ length: 31 }, (_, i) => ({
    id: (i + 1).toString(),
    name: `${i + 1}`,
  }));

  const weekdayOptions = [
    { id: '1', name: 'Monday' },
    { id: '2', name: 'Tuesday' },
    { id: '3', name: 'Wednesday' },
    { id: '4', name: 'Thursday' },
    { id: '5', name: 'Friday' },
    { id: '6', name: 'Saturday' },
    { id: '0', name: 'Sunday' },
  ];

  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    id: i.toString(),
    name: i.toString().padStart(2, '0'),
  }));

  const minuteOptions = Array.from({ length: 60 }, (_, i) => ({
    id: i.toString(),
    name: i.toString().padStart(2, '0'),
  }));

  useEffect(() => {
    if (value) {
      parseCronExpression(value);
    } else {
      resetState();
    }
  }, [value]);

  const resetState = () => {
    //setPrimarySelection('once');
    setMonthSelections([]);
    setDaySelections([]);
    setWeekdaySelections([]);
    setHourSelections([]);
    setMinuteSelections([]);
    setSelectedDate('');
    setTimeSelections([]);
  };

  const parseCronExpression = (cron) => {
    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) return;
    const [minute, hour, day, month, weekday] = parts;

    resetState();

    if (minute === '*' && hour === '*' && day === '*' && month === '*' && weekday === '*' && (primarySelection === 'minute' || value.length >0)) {
      setPrimarySelection('minute');
      return;
    }

    if (minute !== '*' && hour === '*' && day === '*' && month === '*' && weekday === '*' && (primarySelection === 'hour' || value.length >0)) {
      setPrimarySelection('hour');
      setMinuteSelections(minute.split(',').map(m => m.trim()));
      return;
    }

    if (hour !== '*' && day === '*' && month === '*' && weekday === '*' && (primarySelection === 'day' || value.length >2)) {
      setPrimarySelection('day');
      setHourSelections(hour.split(',').map(h => h.trim()));
      setMinuteSelections(minute.split(',').map(m => m.trim()));
      setTimeSelections(
        hour.split(',').map((h, i) => to12HourFormat(h.trim(), minute.split(',')[i]?.trim() || '0'))
      );
      return;
    }

    if (weekday !== '*' && day === '*' && month === '*' && (primarySelection === 'week' || value.length >0 )) {
      setPrimarySelection('week');
      setWeekdaySelections(weekday.split(',').map(w => w.trim()));
      setHourSelections(hour.split(',').map(h => h.trim()));
      setMinuteSelections(minute.split(',').map(m => m.trim()));
      setTimeSelections(
        hour.split(',').map((h, i) => to12HourFormat(h.trim(), minute.split(',')[i]?.trim() || '0'))
      );
      return;
    }

    if (day !== '*' && month === '*' && weekday === '*' && (primarySelection === 'month' || value.length >0)) {
      setPrimarySelection('month');
      setDaySelections(day.split(',').map(d => d.trim()));
      setHourSelections(hour.split(',').map(h => h.trim()));
      setMinuteSelections(minute.split(',').map(m => m.trim()));
      setTimeSelections(
        hour.split(',').map((h, i) => to12HourFormat(h.trim(), minute.split(',')[i]?.trim() || '0'))
      );
      return;
    }

    if (month !== '*' && day !== '*' && weekday === '*' && primarySelection === 'year' && (primarySelection === 'year' || value.length >0)) {
      setPrimarySelection('year');
      setMonthSelections(month.split(',').map(m => m.trim()));
      setDaySelections(day.split(',').map(d => d.trim()));
      setHourSelections(hour.split(',').map(h => h.trim()));
      setMinuteSelections(minute.split(',').map(m => m.trim()));
      setTimeSelections(
        hour.split(',').map((h, i) => to12HourFormat(h.trim(), minute.split(',')[i]?.trim() || '0'))
      );
      return;
    }

    if (!minute.includes(',') && !hour.includes(',') && !day.includes(',') && !month.includes(',') && (primarySelection === 'once' || value.length >0)) {
      setPrimarySelection('once');
      const date = new Date();
      date.setMonth(parseInt(month) - 1);
      date.setDate(parseInt(day));
      date.setHours(parseInt(hour || 0));
      date.setMinutes(parseInt(minute || 0));
      setSelectedDate(formatDateTimeLocal(date));
    }
  };

  const formatDateTimeLocal = (date) => {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date
      .getDate()
      .toString()
      .padStart(2, '0')}T${date.getHours().toString().padStart(2, '0')}:${date
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;
  };

  const to12HourFormat = (hour, minute) => {
    const h = parseInt(hour);
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h % 12 === 0 ? 12 : h % 12;
    return `${displayHour}:${minute.padStart(2, '0')} ${period}`;
  };

  const to24HourFormat = (timeString) => {
    const [time, period] = timeString.split(' ');
    let [hour, minute] = time.split(':').map(num => parseInt(num));
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    return { hour: hour.toString(), minute: minute.toString().padStart(2, '0') };
  };

  const handleDateTimeChange = (dateTimeString) => {
    if (!dateTimeString) return;
    setSelectedDate(dateTimeString);
    setPrimarySelection('once');
  };

  const handleTimeChange = (timeString) => {
    console.log('timeSelections',timeSelections)
   
if (timeSelections.length === 1 && timeSelections[0] === '12:00 AM') {
   setTimeSelections([])
   setHourSelections([])
   setMinuteSelections([])
}
 
    setCurrentTimeInput(timeString);
  };

  const saveTimeSelection = () => {
    if (!currentTimeInput) return;
    const [hour, minute] = currentTimeInput.split(':');
    const timeDisplay = to12HourFormat(hour, minute);
       
    if (!timeSelections.includes(timeDisplay)) {
      setTimeSelections([...timeSelections, timeDisplay]);
      setHourSelections([...hourSelections, hour]);
      setMinuteSelections([...minuteSelections, minute]);
    }
    setCurrentTimeInput('');
    setIsTimePickerOpen(false);
  };

  const removeTimeChip = (timeToRemove) => {
 
    const index = timeSelections.indexOf(timeToRemove);
    if (index !== -1) {
      setTimeSelections(timeSelections.filter((_, i) => i !== index));
      setHourSelections(hourSelections.filter((_, i) => i !== index));
      setMinuteSelections(minuteSelections.filter((_, i) => i !== index));
    }
  };

  const generateCronExpression = () => {
    if (!primarySelection && !allowEmpty) return '';
    let minute = '';
    let hour = '';
    let day = '';
    let month = '';
    let weekday = '';

    switch (primarySelection) {
      case 'once':
        if (selectedDate) {
          const date = new Date(selectedDate);
          minute = date.getMinutes().toString();
          hour = date.getHours().toString();
          day = date.getDate().toString();
          month = (date.getMonth() + 1).toString();
          weekday = '*';
        } else {
          return '';
        }
        break;
      case 'minute':
        minute = '*';
        hour = '*';
        day = '*';
        month = '*';
        weekday = '*';
        break;
      case 'hour':
        minute = minuteSelections.length > 0 ? minuteSelections.join(',') : '';
        hour = '*';
        day = '*';
        month = '*';
        weekday = '*';
        break;
      case 'day':
        minute = minuteSelections.length > 0 ? minuteSelections.join(',') : '';
        hour = hourSelections.length > 0 ? hourSelections.join(',') : '';
        day = '*';
        month = '*';
        weekday = '*';
        break;
      case 'week':
        minute = minuteSelections.length > 0 ? minuteSelections.join(',') : '';
        hour = hourSelections.length > 0 ? hourSelections.join(',') : '';
        day = '*';
        month = '*';
        weekday = weekdaySelections.length > 0 ? weekdaySelections.join(',') : '';
        break;
      case 'month':
        minute = minuteSelections.length > 0 ? minuteSelections.join(',') : '';
        hour = hourSelections.length > 0 ? hourSelections.join(',') : '';
        day = daySelections.length > 0 ? daySelections.join(',') : '';
        month = '*';
        weekday = '*';
        break;
      case 'year':
        minute = minuteSelections.length > 0 ? minuteSelections.join(',') : '';
        hour = hourSelections.length > 0 ? hourSelections.join(',') : '';
        day = daySelections.length > 0 ? daySelections.join(',') : '';
        month = monthSelections.length > 0 ? monthSelections.join(',') : '';
        weekday = '*';
        break;
      default:
        return '';
    }
    return minute === '' && hour === '' && day === '' && month === '' && weekday === '' 
      ? '' 
      : `${minute || '0'} ${hour || '0'} ${day || '1'} ${month || '1'} ${weekday || '*'}`;
  };

  const getHumanReadableText = () => {
    if (!primarySelection) return 'No schedule selected';
    let text = '';
    switch (primarySelection) {
      case 'once':
        if (selectedDate) {
          const date = new Date(selectedDate);
          text = `Once on ${date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })} at ${to12HourFormat(date.getHours(), date.getMinutes().toString().padStart(2, '0'))}`;
        } else {
          text = 'Select a date and time';
        }
        break;
      case 'minute':
        text = 'Every minute';
        break;
      case 'hour':
        text = `Every hour${minuteSelections.length > 0 ? ` at minute${minuteSelections.length > 1 ? 's' : ''} ${minuteSelections.join(', ')}` : ''}`;
        break;
      case 'day':
        text = `Every day${timeSelections.length > 0 ? ` at ${timeSelections.join(', ')}` : ''}`;
        break;
      case 'week':
        text = `Every week${weekdaySelections.length > 0 ? ` on ${weekdaySelections
            .map(id => weekdayOptions.find(w => w.id === id)?.name || id)
            .join(', ')}` : ''}${timeSelections.length > 0 ? ` at ${timeSelections.join(', ')}` : ''}`;
        break;
      case 'month':
        text = `Every month${daySelections.length > 0 ? ` on day${daySelections.length > 1 ? 's' : ''} ${daySelections.join(', ')}` : ''}${timeSelections.length > 0 ? ` at ${timeSelections.join(', ')}` : ''}`;
        break;
      case 'year':
        text = `Every year${monthSelections.length > 0 ? ` in ${monthSelections
            .map(id => monthOptions.find(m => m.id === id)?.name || id)
            .join(', ')}` : ''}${daySelections.length > 0 ? ` on day${daySelections.length > 1 ? 's' : ''} ${daySelections.join(', ')}` : ''}${timeSelections.length > 0 ? ` at ${timeSelections.join(', ')}` : ''}`;
        break;
    }
    return text;
  };

  useEffect(() => {
    const cronExpression = generateCronExpression();
    if (cronExpression !== value) {
      onChange(cronExpression);
    }
  }, [
    primarySelection,
    monthSelections,
    daySelections,
    weekdaySelections,
    hourSelections,
    minuteSelections,
    selectedDate,
    timeSelections,
    
  ]);

  const clearAll = () => {
    resetState();
    onChange('');
  };

  const toggleSelection = (id, selections, setSelections) => {
    if (selections.includes(id)) {
      setSelections(selections.filter(s => s !== id));
    } else {
      setSelections([...selections, id]);
    }
  };

  const handlePrimarySelectionChange = (e) => {
    const selection = e.target.value;
    setPrimarySelection(selection);
    resetState();
    setPrimarySelection(selection);
    // setTimeout(() => {
    //         removeTimeChip('12:00 AM')
    //         }, 500);
    
  };

  const shouldShowMonths = () => primarySelection === 'year';
  const shouldShowDays = () => ['month', 'year'].includes(primarySelection);
  const shouldShowWeekdays = () => primarySelection === 'week';
  const shouldShowTimePicker = () => ['day', 'week', 'month', 'year'].includes(primarySelection);
  const shouldShowMinutes = () => primarySelection === 'hour';
  const shouldShowDatePicker = () => primarySelection === 'once';

  return (
    <div className={` ${disabled ? 'disabled' : ''}`}>
      <style jsx>{`
        .time-chip {
          display: inline-flex;
          align-items: center;
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
          border-radius: 1rem;
          padding: 0.25rem 0.5rem;
          margin: 0.125rem 0.25rem 0.125rem 0;
          font-size: 0.875rem;
        }
        .time-chip-remove {
          margin-left: 0.25rem;
          background: #c3e6cb;
          color: #155724;
          border: none;
          border-radius: 50%;
          width: 1.25rem;
          height: 1.25rem;
          font-size: 0.75rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .time-input-section {
          background: #f8f9fa;
          border: 1px solid #ced4da;
          border-radius: 0.25rem;
          padding: 0.75rem;
          margin-top: 0.5rem;
        }
        .add-time-btn {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
          border-radius: 1rem;
          padding: 0.25rem 0.75rem;
          margin: 0.125rem 0.25rem 0.125rem 0;
          font-size: 0.875rem;
          cursor: pointer;
        }
        .add-time-btn:hover {
          background: #c3e6cb;
        }
        .datetime-input-wrapper {
          position: relative;
          display: block;
          cursor: pointer;
        }
        .datetime-input-wrapper input[type="datetime-local"],
        .datetime-input-wrapper input[type="time"] {
          width: 100%;
          padding: 0.375rem 0.75rem;
          border: 1px solid #ced4da;
          border-radius: 0.25rem;
          cursor: pointer;
          background-color: #fff;
          position: relative;
        }
        .datetime-input-wrapper input[type="datetime-local"]::-webkit-calendar-picker-indicator,
        .datetime-input-wrapper input[type="time"]::-webkit-calendar-picker-indicator {
          background: transparent;
          bottom: 0;
          color: transparent;
          cursor: pointer;
          height: auto;
          left: 0;
          position: absolute;
          right: 0;
          top: 0;
          width: auto;
        }
        .datetime-input-wrapper:hover input {
          border-color: #80bdff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
      `}</style>
      <div className="card">
        <div className="card-body">
          <div className="form-group">
            <label className="font-weight-bold mb-2">Frequency</label>
            <select
              className="form-control mb-3"
              value={primarySelection}
              onChange={handlePrimarySelectionChange}
              disabled={disabled}
            >
              {primaryOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          {shouldShowDatePicker() && (
            <div className="form-group">
              <label className="font-weight-bold">Date and Time</label>
              <div className="datetime-input-wrapper">
                <input
                  id="datetime-input"
                  type="datetime-local"
                  className="form-control"
                  value={selectedDate}
                  onChange={e => handleDateTimeChange(e.target.value)}
                  disabled={disabled}
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
            </div>
          )}

          {shouldShowMonths() && (
            <div className="form-group">
              <label className="font-weight-bold">Months</label>
              <div className="">
                {monthOptions.map(month => (
                    <button
                    key={month.id}
                      type="button"
                      className={`btn m-2 ${
                        monthSelections.includes(month.id) ? 'btn-primary' : 'btn-outline-secondary'
                      }`}
                      onClick={() => toggleSelection(month.id, monthSelections, setMonthSelections)}
                      disabled={disabled}
                    >
                      {month.name}
                    </button>
                 
                ))}
              </div>
            </div>
          )}

          {shouldShowDays() && (
            <div className="form-group">
              <label className="font-weight-bold">Days</label>
              <div className="">
                {dayOptions.map(day => (
                    <button
                    key={day.id}
                      type="button"
                      className={`btn m-2 ${
                        daySelections.includes(day.id) ? 'btn-primary' : 'btn-outline-secondary'
                      }`}
                      onClick={() => toggleSelection(day.id, daySelections, setDaySelections)}
                      disabled={disabled}
                      style={{ minWidth: '50px' }}
                    >
                      {day.name}
                    </button>
                 
                ))}
              </div>
            </div>
          )}

          {shouldShowWeekdays() && (
            <div className="form-group mb-3">
              <label className="font-weight-bold mb-2">Weekdays</label>
              <div >
                {weekdayOptions.map(weekday => (
                    <button
                    key={weekday.id}
                      type="button"
                      className={`btn me-2 ${
                        weekdaySelections.includes(weekday.id) ? 'btn-primary' : 'btn-outline-secondary'
                      }`}
                      onClick={() => toggleSelection(weekday.id, weekdaySelections, setWeekdaySelections)}
                      disabled={disabled}
                    >
                      {weekday.name}
                    </button>
                
                ))}
              </div>
            </div>
          )}

          {shouldShowTimePicker() && (
            <div className="form-group">
              <label className="font-weight-bold">Times</label>
            
              {/* {isTimePickerOpen && ( */}
                <div >
                  <div className="d-flex align-items-center mt-2 mb-3">
                    <div className="datetime-input-wrapper flex-grow-1 me-2">
                      <input
                        id="time-input"
                        type="time"
                        className="form-control"
                        value={currentTimeInput}
                        onChange={e => handleTimeChange(e.target.value)}
                        disabled={disabled}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary me-2"
                      onClick={saveTimeSelection}
                      disabled={disabled || !currentTimeInput}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setCurrentTimeInput('');
                        setIsTimePickerOpen(false);
                      }}
                      disabled={disabled}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              {/* )} */}
                <div>
                {timeSelections.filter(e=>e!=='12:00 AM').map((time, index) => (
                  <span key={index} className="time-chip">
                    {time}
                    <button
                      type="button"
                      className="time-chip-remove"
                      onClick={() => removeTimeChip(time)}
                      disabled={disabled}
                    >
                      ×
                    </button>
                  </span>
                ))}
                {/* <button
                  type="button"
                  className="add-time-btn"
                  onClick={() => setIsTimePickerOpen(true)}
                  disabled={disabled}
                >
                  Add Time
                </button> */}
              </div>
            </div>
          )}

          {shouldShowMinutes() && (
            <div className="form-group">
              <label className="font-weight-bold">Minutes</label>
              <div className="row">
                {minuteOptions.map(minute => (
                  <div key={minute.id} className="col-2 col-sm-1 mb-2">
                    <button
                      type="button"
                      className={`btn btn-block ${
                        minuteSelections.includes(minute.id) ? 'btn-primary' : 'btn-outline-secondary'
                      }`}
                      onClick={() => toggleSelection(minute.id, minuteSelections, setMinuteSelections)}
                      disabled={disabled}
                    >
                      {minute.name}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="alert alert-secondary d-flex justify-content-between align-items-center mt-4">
            <strong>{getHumanReadableText()}</strong>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={clearAll}
              disabled={disabled}
            >
              Clear
            </button>
          </div>

          {/* <div className="alert alert-dark">
            <small>
              <strong>Cron Expression:</strong> {generateCronExpression() || 'None'}
            </small>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default CronGenerator;